import './styles.scss';
import { useEffect, useState } from 'react';
import Parser from 'html-react-parser';
import { Grid } from '@mui/material';

import Card from '../../../common/card';
import Input from '../../../common/input';
import Select from '../../../common/select';
import ButtonCustom from '../../../common/__deprecated__/button-custom';
import TooltipIcon from '../../../common/tooltip-icon';
import { functionalCurrenciesByCountry } from '../../../../config';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { ReactComponent as CalculatorIcon } from '../../../../assets/icons/calculator.svg';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/icons/arrow-right-completed.svg';
import CurrencySelector from '../../../common/currency-selector';
import { useDashboard } from '../../../../store/dashboard';
import { useAvailableCountries } from '../../../../hooks/use-available-countries';
import l from '../../../../lang';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import type { State } from '../../../../store/state';
import { usePaymentLink } from '../../../../store/payment-link';
import { calculate, cleanCalculator, getPaymentMethodsFilter } from '../../../../store/payment-methods/actions';
import MessageError from '../../../common/message-error';

interface CalculatorProps {
  fnCalculate: Function;
  fnGetPaymentMethodsFilter: Function;
  fnCleanCalculator: Function;
  currencies: State['virtualPos']['currencies'];
  loading: State['virtualPos']['loading'];
  calculateResult: State['paymentMethods']['calculateResult'];
  errorInfo: State['paymentMethods']['errorInfo'];
  paymentMethodsFiltered: State['paymentMethods']['paymentMethodsFiltered'];
}

function Calculator({
  calculateResult,
  fnCalculate,
  fnGetPaymentMethodsFilter,
  fnCleanCalculator,
  loading,
  errorInfo,
  paymentMethodsFiltered,
}: CalculatorProps) {
  const { currencies, exchangeRates, cleanErrorPaymentLink, getCurrencies, cleanCurrencies } = usePaymentLink();

  const paymentMethodsOptions = {
    CREDIT_CARD: l('paymentMethods.creditCard'),
    DEBIT_CARD: l('paymentMethods.debitCard'),
    BANK_TRANSFER: l('paymentMethods.bankTransfer'),
    CASH: l('paymentMethods.cash'),
  };

  const { merchantMe } = useDashboard();
  const allCountries = useAvailableCountries();

  const countries = (): { [key: string]: string } => {
    const countries: { [key: string]: string } = {};

    const allowedCountries = merchantMe?.allowLocalToLocal
      ? allCountries
      : allCountries.filter(co => co !== merchantMe?.country);

    allowedCountries.forEach(co => {
      countries[co] = l(`countryCode.${co}`);
    });

    return countries;
  };

  const countriesMap = countries();

  const selectedCountry =
    merchantMe?.country && countriesMap[merchantMe.country] ? merchantMe.country : Object.keys(countriesMap)[0];

  const initialValues = {
    amount: 0,
    amountToReceive: 0,
    currency: '',
    country: selectedCountry,
    paymentMethod: 'CREDIT_CARD',
    crossBorderData: { secondCurrency: 'EUR' },
    paymentMethodId: 0,
  };
  const [isCrossBorder, setIsCrossBorder] = useState(false);
  const [isLocal2Local, setIsLocal2Local] = useState(true);
  const [crossBorderSameCurrency, setCrossBorderSameCurrency] = useState(false);
  const crossBorderCurrency = 'USD';
  const formDisabled = false;

  const applyToFixed = (value: number) => {
    return value && value !== 0 ? value.toFixed(2) : value;
  };

  const validate = (values: typeof initialValues) => {
    const errors: any = {};

    if (!values.amount) {
      errors.amount = l('calculator.error.amountRequired');
    }

    const amount = Number(values.amount);
    const showSecondAmount = !crossBorderSameCurrency && crossBorderCurrency == values.currency;
    const usdAmount = showSecondAmount ? amount : amount / exchangeRates[values.currency];

    if (usdAmount < 1) {
      errors.amount = l('calculator.error.lessThan1Usd');
    }

    return errors;
  };

  const handleSubmit = (values: typeof initialValues) => {
    fnCalculate({
      toCurrency: values.crossBorderData.secondCurrency ? values.crossBorderData.secondCurrency : null,
      amount: values.amount,
      fromCurrency: values.currency,
      paymentMethod: values.paymentMethod,
      country: values.country,
      paymentMethodId: values.paymentMethodId ? values.paymentMethodId : null,
    });
  };

  useEffect(() => {
    if (merchantMe) {
      getCurrencies(selectedCountry);
    }
  }, [merchantMe, selectedCountry]);

  return (
    <Formik initialValues={initialValues} validate={validate} onSubmit={values => handleSubmit(values)}>
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
        const handleOnChangeCurrency = (value: string) => {
          setFieldValue('currency', value);
          setFieldValue('amount', '');
          if (isCrossBorder) {
            setFieldValue('crossBorderData', {
              ...values.crossBorderData,
              secondCurrency: Object.keys(currencies).find(c => c !== value) || '',
              secondAmount: '',
            });
          }

          cleanErrorPaymentLink();
        };

        const setDefaultCurrency = (value: string, isCrossBorder: boolean) => {
          if (currencies) {
            if (!isCrossBorder) {
              const defaultCurrency = (functionalCurrenciesByCountry as { [key: string]: string })[value];
              handleOnChangeCurrency(defaultCurrency);
            } else {
              if (currencies['USD']) {
                handleOnChangeCurrency('USD');
              } else {
                const cs = Object.keys(currencies);
                if (cs.length && !cs.filter(d => value === d).length) {
                  handleOnChangeCurrency(cs[0]);
                }
              }
            }
          }
        };

        useEffect(() => {
          if (values.country) {
            setIsCrossBorder(true);
            setCrossBorderSameCurrency(!Object.keys(currencies).find(c => c !== crossBorderCurrency));
            setDefaultCurrency(values.country, true);
          }
        }, [currencies]);

        return (
          <Grid container spacing={2} sx={{ marginTop: '30px' }}>
            <Grid item xs={12} md={6}>
              <div className="calculator-description">
                <span className="calculator-icon">
                  <CalculatorIcon />
                </span>
                <h2>{l('calculator.title')}</h2>
                <p>{l('calculator.description')}</p>
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                {(errorInfo?.title || errorInfo?.message) && Object.keys(errors).length === 0 ? (
                  <div className="create-payment-link-form__error">
                    <MessageError errorInfo={errorInfo} />
                  </div>
                ) : null}
                <form onSubmit={handleSubmit}>
                  <div className="create-payment-link-form__container">
                    <div
                      className={`create-payment-link-form__row ${
                        values.country && isCrossBorder && !crossBorderSameCurrency ? 'shrunk' : ''
                      }`}
                    >
                      <div className="create-payment-link-form__field">
                        <Select
                          options={countries()}
                          name="country"
                          label={l('paymentLink.country')}
                          value={values.country}
                          onChange={e => {
                            cleanCurrencies();
                            getCurrencies(e.target.value);
                            setIsLocal2Local(e.target.value === merchantMe?.country);
                            setFieldValue('country', e.target.value);
                            if (values.paymentMethod === 'CASH') {
                              fnGetPaymentMethodsFilter(values.paymentMethod, e.target.value);
                            }
                          }}
                        />
                      </div>
                      <div className="create-payment-link-form__info">
                        <TooltipIcon
                          placement="bottom-end"
                          text={Parser(l('paymentLink.country.tooltip'))}
                          icon={<InfoIcon />}
                        />
                      </div>
                    </div>
                    <div
                      className={`create-payment-link-form__row ${
                        values.country && isCrossBorder && !crossBorderSameCurrency ? 'shrunk' : ''
                      }`}
                    >
                      <div className="create-payment-link-form__field">
                        <Select
                          options={paymentMethodsOptions}
                          name="payment_method"
                          label={l('transactions.paymentMethod.type')}
                          value={values.paymentMethod}
                          onChange={e => {
                            if (e.target.value === 'CASH') {
                              fnGetPaymentMethodsFilter(e.target.value, values.country);
                            }
                            setFieldValue('paymentMethod', e.target.value);
                          }}
                        />
                      </div>
                    </div>
                    {Object.keys(paymentMethodsFiltered ?? {}).length > 0 && values.paymentMethod == 'CASH' && !loading && (
                      <div
                        className={`create-payment-link-form__row ${
                          values.country && isCrossBorder && !crossBorderSameCurrency ? 'shrunk' : ''
                        }`}
                      >
                        <div className="create-payment-link-form__field">
                          <Select
                            options={paymentMethodsFiltered}
                            name="payment_method"
                            label={l('transactions.paymentMethod')}
                            value={values.paymentMethodId}
                            onChange={e => {
                              setFieldValue('paymentMethodId', e.target.value);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {!isCrossBorder ? (
                      <>
                        <div className="create-payment-link-form__row">
                          <div className="create-payment-link-form__field amount">
                            <Input
                              type="number"
                              name="amount"
                              label={l('paymentLink.amountWantToCharge')}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              onFocus={() => fnCleanCalculator()}
                              value={values.amount}
                              disabled={formDisabled || !values.currency}
                              error={(touched.amount && errors.amount) || undefined}
                              prefix={values.currency}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {!crossBorderSameCurrency && (
                          <CurrencySelector
                            firstCurrency={crossBorderCurrency}
                            secondCurrency={Object.keys(currencies).find(c => c !== crossBorderCurrency) || ''}
                            value={values.currency}
                            onChange={value => handleOnChangeCurrency(value)}
                            disabled={formDisabled}
                          />
                        )}
                        <div
                          className={`create-payment-link-form__row calculator_row ${
                            !crossBorderSameCurrency && crossBorderCurrency === values.currency ? 'shrunk' : ''
                          }`}
                        >
                          <div
                            className={`create-payment-link-form__field calculator_field ${
                              crossBorderCurrency == values.currency && !crossBorderSameCurrency
                                ? 'amount_cross_border amount_cross_border_left'
                                : ''
                            }`}
                          >
                            <Input
                              type="number"
                              name="amount"
                              onFocus={() => fnCleanCalculator()}
                              label={l(
                                crossBorderCurrency === values.currency
                                  ? 'paymentLink.amountWantToCharge'
                                  : 'paymentLink.amountYourClientWillPay'
                              )}
                              onChange={e => {
                                const value = e.target.value;
                                if (crossBorderCurrency === values.currency) {
                                  setFieldValue('crossBorderData', {
                                    ...values.crossBorderData,
                                    secondAmount: applyToFixed(value * exchangeRates[values.crossBorderData.secondCurrency]),
                                  });
                                } else {
                                  setFieldValue('crossBorderData', {
                                    ...values.crossBorderData,
                                    secondAmount: applyToFixed(value / exchangeRates[values.currency]),
                                  });
                                }
                                setFieldValue('amount', value);
                              }}
                              onBlur={handleBlur}
                              value={values.amount}
                              disabled={formDisabled || !values.currency}
                              error={(touched.amount && errors.amount) || undefined}
                              prefix={values.currency}
                            />
                            {!crossBorderSameCurrency && crossBorderCurrency != values.currency && (
                              <div>
                                <p className="create-payment-link-form__exchange_rate">
                                  <span>{l('paymentLink.exchangeRate')}</span>
                                  <span>
                                    &nbsp;{values.currency}{' '}
                                    {!isLocal2Local
                                      ? applyToFixed(exchangeRates[values.currency])
                                      : applyToFixed(exchangeRates.base)}
                                  </span>
                                  <ArrowRightIcon />
                                  <span>
                                    <span> {values.crossBorderData.secondCurrency} </span>
                                    {applyToFixed(exchangeRates[values.crossBorderData.secondCurrency])}
                                  </span>
                                </p>
                              </div>
                            )}
                            {crossBorderSameCurrency && (
                              <div className={`create-payment-link-form__info input`}>
                                <TooltipIcon
                                  placement="right"
                                  text={Parser(l('paymentLink.tooltip.sameCurrency', '***'))}
                                  icon={<InfoIcon />}
                                />
                              </div>
                            )}
                          </div>
                          {!crossBorderSameCurrency && crossBorderCurrency == values.currency && (
                            <div className="create-payment-link-form__field calculator_field amount_cross_border amount_cross_border_right">
                              <Input
                                type="number"
                                name="second_amount"
                                disabled
                                value={applyToFixed(calculateResult?.toAmount ?? 0)}
                                label={l('paymentLink.amountYourClientWillPay')}
                                prefix={values.crossBorderData.secondCurrency}
                              />
                              {crossBorderCurrency !== values.currency && (
                                <div>
                                  <p className="create-payment-link-form__fx_message">
                                    <span>{l('paymentLink.exchangeRate.fxMessage')}</span>
                                  </p>
                                </div>
                              )}
                              <div className={`create-payment-link-form__info input`}>
                                <TooltipIcon
                                  placement="bottom-end"
                                  text={Parser(l('currencySelector.tooltip'))}
                                  icon={<InfoIcon />}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        {!crossBorderSameCurrency && crossBorderCurrency == values.currency && (
                          <div>
                            <p className="create-payment-link-form__exchange_rate">
                              <span>{l('paymentLink.exchangeRate')}</span>
                              <span>
                                &nbsp;{values.currency} {applyToFixed(exchangeRates[values.currency])}
                              </span>
                              <ArrowRightIcon />
                              <span>
                                <span> {values.crossBorderData.secondCurrency} </span>
                                {!isLocal2Local
                                  ? applyToFixed(exchangeRates[values.crossBorderData.secondCurrency])
                                  : applyToFixed(exchangeRates.base)}
                              </span>
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    <div style={{ paddingBottom: '18px' }}>
                      <div className="create-payment-link-form__field calculator-submit">
                        <ButtonCustom
                          type="submit"
                          loading={loading}
                          name="btn-confirm"
                          text={l('calculator.calculate')}
                          variant="contained"
                          fullWidth
                        />
                      </div>
                    </div>
                    <div className="create-payment-link-form__divider" />
                    <div className="create-payment-link-form__row">
                      <div className="create-payment-link-form__field calculator_field">
                        <Input
                          type="number"
                          name="amount"
                          label={l('paymentLink.amountYouWillReceive')}
                          value={applyToFixed(calculateResult?.merchantBalanceAmount ?? 0)}
                          disabled
                          prefix={merchantMe?.functionalCurrency}
                        />
                        <div className="calculator-note">{l('calculator.note')}</div>
                      </div>
                    </div>
                  </div>
                </form>
              </Card>
            </Grid>
          </Grid>
        );
      }}
    </Formik>
  );
}

const mapStateToProps = (state: State) => ({
  loading: state.paymentMethods.loading,
  error: state.paymentMethods.calculateError,
  errorInfo: state.paymentMethods.calculateErrorInfo,
  calculateResult: state.paymentMethods.calculateResult,
  paymentMethodsFiltered: state.paymentMethods.paymentMethodsFiltered,
});

const mapDispatchToProps = (dispatch: Function) => ({
  fnCalculate: (payload: any) => dispatch(calculate(payload)),
  fnCleanCalculator: () => dispatch(cleanCalculator()),
  fnGetPaymentMethodsFilter: (paymentType: string, country: string) =>
    dispatch(getPaymentMethodsFilter(paymentType, country)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Calculator);
